import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { ICurationItem } from '../../../interfaces/curation-item.interface';
import { relatedEntitiesRevisionTable } from '../../../classes/relatedEntitiesRevisionTable';
import { CurationActionCreatorService } from '../../../services/curation-action-creator.service';
import { CurationSettings, CurationTypes } from '../../curation-dashboard/curation-setting';
import { Observable, Subject } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import * as _ from 'lodash';
import { IRevision } from '@ispm/core/interfaces/workflow.interface';
import { CurationItemPermissions, CurationItemWritePermissionsFilter } from '../curation-enums';
import { IWorkflowRequest } from '@ispm/test/interfaces/diagnostic-order.interface';
import { selectActiveWorkflow } from '@ispm/workflow/store/workflow-config.selectors';
import { filter, takeUntil } from 'rxjs/operators';
import { StringUtils } from '@ispm/shared/utils/String.utils';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { IWorkflow } from '@ispm/core/interfaces/workflow.interface';
import {CURATION_EDITOR_ACTIONS} from "../../../store/curation.editor.actions";

@Component({
  selector: 'gen-curation-editor-actions',
  templateUrl: './curation-editor-actions.component.html',
  styleUrls: ['./curation-editor-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class CurationEditorActionsComponent implements OnInit, OnDestroy {
  @Input() curationStatus: any;
  @Input() curationKey: any;
  @Input() approvalUserMode = false;
  @Input() publishUserMode = false;
  @Input() curatorUserMode = false;
  @Input() isAssignee = false;
  @Input() buttonsDisabled = false;
  @Input() evidence: any;
  @Input() oldRevisionMessage: string;
  @Input() oldRevisionIsShown: boolean = false;
  @Input() readOnlyUserMode: boolean = false;
  @Input() multiStepReview: boolean = false;
  @Input() searchFormConfigParams: any;
  @Input() searchDataConfigParams: any;
  @Input() googleSearchURL: string;
  @Input() pubmedSearchURL: string;
  @Input() curationDataForSearch: { [key: string]: string; } = { };
  @Input() revisedRelatedEntities = [];
  @Input() revisiedEntitiesTableData: relatedEntitiesRevisionTable;
  @Input() showPreviewButton: boolean = false;
  @Input() sameOrganization: boolean = false;
  @Input() nodesWithNotes: { id: string; name: string }[];
  @Input() newNotes: number;
  @Input() reassignButtonDisabled = false;
  @Input() showStopWorkflowButton: boolean = true;
  @Input() showArchiveReason: boolean = true;

  private _historyData: Array<{ revision: IRevision, item: { [prop: string]: any } }>;
  private revisionsNumber: number = 0;
  protected readonly CURATION_EDITOR_ACTIONS = CURATION_EDITOR_ACTIONS;

  @Input() set historyData(data: Array<{ revision: IRevision, item: { [prop: string]: any } }>) {
    if (data) {
      this._historyData = data;
      this.revisionsNumber = this.historyData.length;
    }
  }

  get historyData() {
    return this._historyData;
  }

  @Output() statusChanged: EventEmitter<any> = new EventEmitter();
  @Output() statusChangedToRejected: EventEmitter<any> = new EventEmitter();
  @Output() whiteListFieldChanged: EventEmitter<any> = new EventEmitter();
  @Output() goBackEvent: EventEmitter<any> = new EventEmitter();
  @Output() linkClickedEvent: EventEmitter<any> = new EventEmitter();
  @Output() notesPanelToggled: EventEmitter<void> = new EventEmitter<void>();
  @Output() runPreview: EventEmitter<any> = new EventEmitter();
  @Output() toggleReassignCurationItem: EventEmitter<any> = new EventEmitter();
  @Output() toggleSubmitCurationItemForReview: EventEmitter<any> = new EventEmitter();
  @Output() toggleShowArchiveReason: EventEmitter<any> = new EventEmitter();

  private _curationItem: ICurationItem;
  public popupTableShown: boolean = false;
  public approveCommand: string = CurationSettings.CURATION_STATUS_APPROVED;
  public publishCommand: string = CurationSettings.CURATION_STATUS_PUBLISHED;
  public publishedSubmittedCommand: string = CurationSettings.CURATION_STATUS_PUBLISHED_SUBMITTED;
  public inreviewCommand: string = CurationSettings.CURATION_STATUS_INREVIEW;
  public waitForArchiveCommand: string = CurationSettings.CURATION_STATUS_WAITING_FOR_ARCHIVE;
  public waitForApprovalCommand: string = CurationSettings.CURATION_STATUS_WAITING_FOR_APPROVAL;
  public draftCommand: string = CurationSettings.CURATION_STATUS_DRAFT;
  public archiveSubmittedCommand: string = CurationSettings.CURATION_STATUS_ARCHIVED_SUBMITTED;
  public rejectedCommand: string = CurationSettings.CURATION_STATUS_REJECTED;
  public approvedcommand: string = CurationSettings.CURATION_STATUS_APPROVED;
  public archiveEnabled: boolean = false;
  public archiveAlwaysEnabled: boolean = false;
  public rejectArchiveCommand: string = CurationSettings.CURATION_STATUS_APPROVED; // getting back to the last approved
  public multiStepRejectArchiveCommand: string = CurationSettings.CURATION_STATUS_PUBLISHED; // getting back to the last published
  private destroyed$: Subject<void> = new Subject<void>();

  public discardEnabled: boolean = false;
  public discardEnabledForStatuses: string[];
  public discardCommand: string = CurationSettings.CURATION_VIRTUAL_STATUS_DISCARD;
  public workflowRequest$: Observable<IWorkflowRequest>;
  public previousStatus: string = null;
  constructor(private curationActionCreatorService: CurationActionCreatorService,
              private genomicsStore: Store<IgenomicsState>,
              private authService: AuthenticationService,
              public detector?: ChangeDetectorRef) {
  }

  @Input() set curationItem(item: ICurationItem) {
    this._curationItem = item;
  }

  public getCurationType() {
    return _.has(this._curationItem, 'curationItem.type') ? CurationTypes[_.get(this._curationItem, 'curationItem.type', '')] :
      _.has(this._curationItem, 'aberration') ? CurationTypes['aberration'] : '';
  }

  public getDiscipline(): string {
    return _.get(this._curationItem, 'curationData.content.discipline.display', '');
  }

  getRejectArchiveCommand(): string {
    return this.getNormalizedPreviousStatus(); // this.multiStepReview ? this.multiStepRejectArchiveCommand : this.rejectArchiveCommand;
  }

  get curationItem(): ICurationItem {
    return this._curationItem;
  }

  get revisedRelatedEntitiesTitle(): string {
    const title = [];

    this.revisedRelatedEntities.forEach((entity) => {
      const statusText: string = entity.latestStatus == 'archivedsubmitted' ? 'archived' : 'revised';
      title.push(entity.curationItemType + ' was ' + statusText);
    });
    return title.join(', ');
  }

  ngOnInit() {
    this.curationStatusUpdate();
    this.reassignButtonDisabled = this.reassignButtonDisabled || this.curationStatus === CurationSettings.CURATION_STATUS_APPROVED ||
      this.curationStatus === CurationSettings.CURATION_STATUS_PUBLISHED;

    this.workflowRequest$ = this.genomicsStore.pipe(
      takeUntil(this.destroyed$),
      select(selectActiveWorkflow),
      filter(workflow => !!workflow)
    );

    this.genomicsStore.pipe(takeUntil(this.destroyed$.asObservable()), select(selectActiveWorkflow), filter(workflow => !!workflow))
      .subscribe(workflow =>  this.getPreviousStatus(workflow));
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }


  ngOnChanges(){
    this.curationStatusUpdate();
    this.detector.detectChanges();
  }

  curationStatusUpdate(){
    this.curationActionCreatorService.getState(['definitions'])
    .take(1)
    .subscribe((data => {
      const curationType: string = this._curationItem?.curationItem
        ? this._curationItem['curationItem']['type']
        : this._curationItem?.type // support "old" curationitems object like aberration)
      ;
      const conf = Array.from(data.values()).filter(data => data['type'] === curationType)[0];
      if (conf && conf['ui']['workflow']) {
        this.archiveEnabled = _.get(conf, 'ui.workflow.archiveEnabled', false);
        this.approveCommand = _.get(conf, 'ui.workflow.workflowVersion', '') === 'V1' ? CurationSettings.CURATION_STATUS_APPROVED_SUBMITTED : CurationSettings.CURATION_STATUS_APPROVED;
        this.archiveAlwaysEnabled = _.get(conf, 'ui.workflow.archiveAlwaysEnabled', false);

        // 'discard' command data
        const permissionExists = [CurationItemPermissions.WRITE_CURATION_ITEM_PERMISSION,
          CurationItemWritePermissionsFilter.replace('*', curationType.toUpperCase())]
          .some(permission => this.authService.doesUserHasPermission(permission));
        this.discardEnabled = permissionExists && _.get(conf, 'ui.workflow.discard.enabled', false);
        this.discardEnabledForStatuses = _.get(conf, 'ui.workflow.discard.enabledForStatuses',
          [CurationSettings.CURATION_STATUS_APPROVED, CurationSettings.CURATION_STATUS_PUBLISHED]);
      }

    }));
  }
  updateStatus(status: string, action?: string) {
    if (action) {
      this.curationActionCreatorService.setLastEditorAction(action);
    }

    switch (status) {
      case CurationSettings.CURATION_STATUS_REJECTED:
        this.statusChangedToRejected.emit();
        break;
      case CurationSettings.CURATION_STATUS_ARCHIVED_SUBMITTED:
        this.executeArchiveSubmittedCommand();
        break;
      default:
        this.executeAction(status);
        break;
    }
  }

  saveWhiteListChanges() {
    // To update the form by using the outside-click directive
    window.document.body.click();
    this.whiteListFieldChanged.emit();
  }

  goBack() {
    this.goBackEvent.emit();
  }

  showPopupTablePanel() {
    this.popupTableShown = !this.popupTableShown;
  }

  linkClicked($event, nodeId, params?) {
    event.stopPropagation();
    this.linkClickedEvent.emit({ nodeId, params});
  }

  toggleNotesPanel() {
    this.notesPanelToggled.emit();
  }

  executeStartPreview() {
    this.runPreview.emit();
  }

  shouldShowRevisedRelatedEntitiesTable(): boolean {
    return (!_.isEmpty(this.revisedRelatedEntities))
      && this.revisiedEntitiesTableData
      && (!_.isEmpty(this.revisiedEntitiesTableData.columns));
  }

  shouldShowNodesWithNotesTable(): boolean {
    return (!_.isEmpty(this.nodesWithNotes));
  }

  shouldShowLinkTable(): boolean {
    return (!this.oldRevisionIsShown) && (this.shouldShowRevisedRelatedEntitiesTable() || this.shouldShowNodesWithNotesTable());
  }

  executeReassignCurationItem() {
    this.toggleReassignCurationItem.emit();
  }

  executeSubmitCurationItemForReview() {
    this.toggleSubmitCurationItemForReview.emit();
  }

  public showDiscardForCurrentStatus() {
    // check if curationItem has previous versions
    return this.revisionsNumber > 1 && this.discardEnabled && this.discardEnabledForStatuses.find(item => item === this.curationStatus);
  }

  isSaveButtonDisabled() {
    return !(this.approvalUserMode || this.publishUserMode);
  }

  escapeChevron(val: string): string {
    return StringUtils.escapeChevron(val);
  }

  private getPreviousStatus(workflow: IWorkflow) {
    if (!workflow.activeRevision) {
      this.previousStatus = null;
    }
    workflow.revisions.forEach((revision) => {
      if (revision.id === workflow.activeRevision) {
        revision.items.forEach(item => {
          if (item.id === revision.activeJobId) {
            if (item.inputs.length > 0) {
              item.inputs.forEach((input) => {
                if (_.has(input, 'name') && input.name === CurationSettings.CURATION_STATUS_PREVIOUS_STATUS) {
                  this.previousStatus = input.value;
                }
              });
            }
          }
        });
      }
    });
  }

  private getNormalizedPreviousStatus() {
    switch (this.previousStatus) {
      case CurationSettings.CURATION_STATUS_DRAFT:
      case CurationSettings.CURATION_STATUS_INREVIEW:
        return this.previousStatus;
      case  CurationSettings.CURATION_STATUS_PUBLISHED_SUBMITTED:
        return this.multiStepRejectArchiveCommand;
      case  CurationSettings.CURATION_STATUS_APPROVED_SUBMITTED:
        return this.rejectArchiveCommand;
    }
    return this.multiStepReview ? this.multiStepRejectArchiveCommand : this.rejectArchiveCommand;
  }

  private executeAction(status: string) {
    window.document.body.click();
    this.statusChanged.emit({ status: status});
  }

  private executeArchiveSubmittedCommand() {
    if (this.sameOrganization && !this.readOnlyUserMode && this.curationStatus === CurationSettings.CURATION_STATUS_WAITING_FOR_ARCHIVE && this.showArchiveReason) {
      this.toggleShowArchiveReason.emit();
    }
    else {
      this.executeAction(CurationSettings.CURATION_STATUS_WAITING_FOR_ARCHIVE);
    }
  }
}
